/**
 * /developpeurs — API keys and outgoing webhooks for the company.
 *
 * Keys are generated client-side (dp_live_ + 40 base62 chars), shown once,
 * and only their SHA-256 hash is stored (api_keys.key_hash). Webhook secrets
 * are stored so the platform can sign deliveries; the customer verifies
 * X-Delapresse-Signature = sha256=HMAC(secret, raw body).
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Copy, KeyRound, Plus, Trash2, Webhook, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePackageAccess } from "@/lib/packageAccess";
import { useCompany } from "@/hooks/useCompany";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EVENTS = ["article.created", "article.published", "article.qa_failed", "article.translated", "publish.failed"] as const;
// api_keys / webhook_* are newer than the generated Supabase types.
const db = supabase as any;
const API_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-api/v1`;

function randomToken(len: number) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}
async function sha256Hex(s: string) {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function DeveloperPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const access = usePackageAccess();
  const { currentCompanyId } = useCompany();
  const qc = useQueryClient();
  const companyId = currentCompanyId ?? null;
  const planSlug: string = String(access.package?.slug ?? "");
  const apiLevel = /agency|enterprise/.test(planSlug) ? "full_webhooks" : /network/.test(planSlug) ? "full" : /growth/.test(planSlug) ? "read" : "none";
  const canRead = apiLevel !== "none";
  const canWrite = apiLevel === "full" || apiLevel === "full_webhooks";
  const canHooks = apiLevel === "full_webhooks";

  const { data: company } = useQuery({
    queryKey: ["dev-company", user?.id],
    enabled: !!user && !companyId,
    queryFn: async () => {
      const { data } = await supabase.rpc("default_company_id", { _user: user!.id });
      return data as string | null;
    },
  });
  const cid = companyId || company || null;

  const keysQ = useQuery({
    queryKey: ["api-keys", cid],
    enabled: !!cid,
    queryFn: async () => {
      const { data, error } = await db.from("api_keys").select("id, name, key_prefix, scopes, last_used_at, revoked_at, created_at").eq("company_id", cid!).order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
  const hooksQ = useQuery({
    queryKey: ["webhooks", cid],
    enabled: !!cid,
    queryFn: async () => {
      const { data, error } = await db.from("webhook_endpoints").select("id, url, events, description, is_active, last_delivery_at, last_status, consecutive_failures, created_at").eq("company_id", cid!).order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
  const deliveriesQ = useQuery({
    queryKey: ["webhook-deliveries", cid],
    enabled: !!cid,
    queryFn: async () => {
      const { data, error } = await db.from("webhook_deliveries").select("id, endpoint_id, event, status_code, error, duration_ms, created_at").eq("company_id", cid!).order("created_at", { ascending: false }).limit(30);
      if (error) throw error;
      return data || [];
    },
  });

  // ---- keys
  const [keyName, setKeyName] = useState("");
  const [keyWrite, setKeyWrite] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const createKey = useMutation({
    mutationFn: async () => {
      const plaintext = `dp_live_${randomToken(40)}`;
      const hash = await sha256Hex(plaintext);
      const { error } = await db.from("api_keys").insert({
        company_id: cid!, user_id: user!.id, name: keyName.trim() || "default",
        key_prefix: plaintext.slice(0, 16), key_hash: hash, scopes: keyWrite ? ["read", "write"] : ["read"],
      });
      if (error) throw error;
      return plaintext;
    },
    onSuccess: (plaintext) => { setRevealed(plaintext); setKeyName(""); setKeyWrite(false); qc.invalidateQueries({ queryKey: ["api-keys", cid] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const revokeKey = useMutation({
    mutationFn: async (id: string) => { const { error } = await db.from("api_keys").update({ revoked_at: new Date().toISOString() }).eq("id", id); if (error) throw error; },
    onSuccess: () => { toast.success(t("developer.keys.revoked")); qc.invalidateQueries({ queryKey: ["api-keys", cid] }); },
  });

  // ---- webhooks
  const [hookUrl, setHookUrl] = useState("");
  const [hookDesc, setHookDesc] = useState("");
  const [hookEvents, setHookEvents] = useState<string[]>(["article.published"]);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const createHook = useMutation({
    mutationFn: async () => {
      if (!/^https:\/\//.test(hookUrl)) throw new Error(t("developer.webhooks.httpsOnly"));
      const secret = `whsec_${randomToken(32)}`;
      const { error } = await db.from("webhook_endpoints").insert({ company_id: cid!, url: hookUrl.trim(), secret, events: hookEvents, description: hookDesc.trim() || null, created_by: user!.id });
      if (error) throw error;
      return secret;
    },
    onSuccess: (secret) => { setRevealedSecret(secret); setHookUrl(""); setHookDesc(""); qc.invalidateQueries({ queryKey: ["webhooks", cid] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const toggleHook = useMutation({
    mutationFn: async ({ id, on }: { id: string; on: boolean }) => { const { error } = await db.from("webhook_endpoints").update({ is_active: on, consecutive_failures: 0 }).eq("id", id); if (error) throw error; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks", cid] }),
  });
  const deleteHook = useMutation({
    mutationFn: async (id: string) => { const { error } = await db.from("webhook_endpoints").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { toast.success(t("developer.webhooks.deleted")); qc.invalidateQueries({ queryKey: ["webhooks", cid] }); },
  });

  const copy = async (s: string) => { await navigator.clipboard.writeText(s); toast.success(t("developer.copied")); };
  const curl = useMemo(() => `curl -H "Authorization: Bearer dp_live_..." \\\n  "${API_BASE}/articles?limit=10"`, []);

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t("developer.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("developer.subtitle")}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge variant={canRead ? "default" : "secondary"}>{t("developer.plan.read")}: {canRead ? t("developer.plan.on") : t("developer.plan.off")}</Badge>
          <Badge variant={canWrite ? "default" : "secondary"}>{t("developer.plan.write")}: {canWrite ? t("developer.plan.on") : t("developer.plan.off")}</Badge>
          <Badge variant={canHooks ? "default" : "secondary"}>{t("developer.plan.webhooks")}: {canHooks ? t("developer.plan.on") : t("developer.plan.off")}</Badge>
        </div>
        {!canRead && <p className="mt-2 text-sm text-amber-700">{t("developer.plan.upgradeHint")}</p>}
      </header>

      {/* API keys */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4" /> {t("developer.keys.title")}</CardTitle>
          <CardDescription>{t("developer.keys.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">{t("developer.keys.name")}</Label>
              <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="cms-sync" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={keyWrite} disabled={!canWrite} onCheckedChange={(v) => setKeyWrite(v === true)} />
              {t("developer.keys.writeScope")}
            </label>
            <Button onClick={() => createKey.mutate()} disabled={!cid || !canRead || createKey.isPending}><Plus className="mr-2 h-4 w-4" />{t("developer.keys.create")}</Button>
          </div>
          <ul className="divide-y divide-border rounded-md border border-border">
            {(keysQ.data || []).map((k) => (
              <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <div>
                  <span className="font-medium">{k.name}</span>
                  <code className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs">{k.key_prefix}…</code>
                  <span className="ml-2 text-xs text-muted-foreground">{(k.scopes || []).join(", ")}</span>
                  {k.revoked_at && <Badge variant="secondary" className="ml-2">{t("developer.keys.revokedBadge")}</Badge>}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{k.last_used_at ? t("developer.keys.lastUsed", { date: new Date(k.last_used_at).toLocaleString() }) : t("developer.keys.neverUsed")}</span>
                  {!k.revoked_at && <Button size="sm" variant="ghost" onClick={() => revokeKey.mutate(k.id)}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </li>
            ))}
            {keysQ.data?.length === 0 && <li className="px-3 py-3 text-sm text-muted-foreground">{t("developer.keys.empty")}</li>}
          </ul>
          <div className="rounded-md bg-muted p-3">
            <p className="mb-2 text-xs font-medium">{t("developer.keys.example")}</p>
            <pre className="overflow-x-auto text-xs"><code>{curl}</code></pre>
            <p className="mt-2 text-xs text-muted-foreground">
              GET /v1/me · GET /v1/websites · GET /v1/categories · GET /v1/articles · GET /v1/articles/:id · POST /v1/articles · PATCH /v1/articles/:id · POST /v1/articles/:id/publish · DELETE /v1/articles/:id
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Webhook className="h-4 w-4" /> {t("developer.webhooks.title")}</CardTitle>
          <CardDescription>{t("developer.webhooks.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">URL (https)</Label>
              <Input value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://example.com/hooks/delapresse" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("developer.webhooks.descriptionField")}</Label>
              <Input value={hookDesc} onChange={(e) => setHookDesc(e.target.value)} />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            {EVENTS.map((ev) => (
              <label key={ev} className="flex items-center gap-2 text-sm">
                <Checkbox checked={hookEvents.includes(ev)} onCheckedChange={(v) => setHookEvents((p) => v === true ? Array.from(new Set([...p, ev])) : p.filter((x) => x !== ev))} />
                <code className="text-xs">{ev}</code>
              </label>
            ))}
          </div>
          <Button onClick={() => createHook.mutate()} disabled={!cid || !canHooks || createHook.isPending || hookEvents.length === 0}><Plus className="mr-2 h-4 w-4" />{t("developer.webhooks.create")}</Button>
          {!canHooks && <p className="text-xs text-amber-700">{t("developer.webhooks.planHint")}</p>}

          <ul className="divide-y divide-border rounded-md border border-border">
            {(hooksQ.data || []).map((h) => (
              <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="truncate font-mono text-xs">{h.url}</div>
                  <div className="text-xs text-muted-foreground">{(h.events || []).join(", ")}{h.description ? ` · ${h.description}` : ""}</div>
                  <div className="text-xs text-muted-foreground">
                    {h.last_delivery_at ? `${t("developer.webhooks.last")} ${new Date(h.last_delivery_at).toLocaleString()} · HTTP ${h.last_status ?? "—"}` : t("developer.webhooks.noDelivery")}
                    {h.consecutive_failures > 0 && ` · ${t("developer.webhooks.failures", { n: h.consecutive_failures })}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={h.is_active} onCheckedChange={(on) => toggleHook.mutate({ id: h.id, on })} />
                  <Button size="sm" variant="ghost" onClick={() => deleteHook.mutate(h.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </li>
            ))}
            {hooksQ.data?.length === 0 && <li className="px-3 py-3 text-sm text-muted-foreground">{t("developer.webhooks.empty")}</li>}
          </ul>

          {(deliveriesQ.data || []).length > 0 && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <p className="text-xs font-medium">{t("developer.webhooks.deliveries")}</p>
                <Button size="sm" variant="ghost" onClick={() => deliveriesQ.refetch()}><RefreshCw className="h-3.5 w-3.5" /></Button>
              </div>
              <ul className="max-h-64 overflow-y-auto rounded-md border border-border text-xs">
                {deliveriesQ.data!.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5 last:border-0">
                    <span className="font-mono">{d.event}</span>
                    <span className={d.status_code && d.status_code < 300 ? "text-emerald-600" : "text-red-600"}>{d.status_code ?? "—"}{d.error ? ` ${d.error}` : ""}</span>
                    <span className="text-muted-foreground">{d.duration_ms} ms · {new Date(d.created_at).toLocaleTimeString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{t("developer.webhooks.signature")}</p>
        </CardContent>
      </Card>

      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("developer.keys.revealTitle")}</DialogTitle><DialogDescription>{t("developer.keys.revealDescription")}</DialogDescription></DialogHeader>
          <code className="block break-all rounded bg-muted p-3 text-sm">{revealed}</code>
          <DialogFooter><Button onClick={() => revealed && copy(revealed)}><Copy className="mr-2 h-4 w-4" />{t("developer.copy")}</Button><Button variant="outline" onClick={() => setRevealed(null)}><Check className="mr-2 h-4 w-4" />{t("developer.done")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!revealedSecret} onOpenChange={(o) => !o && setRevealedSecret(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("developer.webhooks.secretTitle")}</DialogTitle><DialogDescription>{t("developer.webhooks.secretDescription")}</DialogDescription></DialogHeader>
          <code className="block break-all rounded bg-muted p-3 text-sm">{revealedSecret}</code>
          <DialogFooter><Button onClick={() => revealedSecret && copy(revealedSecret)}><Copy className="mr-2 h-4 w-4" />{t("developer.copy")}</Button><Button variant="outline" onClick={() => setRevealedSecret(null)}><Check className="mr-2 h-4 w-4" />{t("developer.done")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
